import { PreOwnedGuarantees } from '../components/PreOwnedGuarantees';
import { SizeStockAlert } from '../components/SizeStockAlert';
import { SizeGuide } from '../components/SizeGuide';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Heart, ShoppingCart, Truck, Shield, RotateCcw, ChevronRight, Minus, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ProductCard, { GRADE_STYLE } from '../components/ProductCard';
import EuGuaranteeNotice from '../components/EuGuaranteeNotice';
import { AddToRegistryButton } from '../components/AddToRegistryButton';
import GaranLabel from '../components/GaranLabel';
import StarRateBadge from '../components/StarRateBadge';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { trackViewItem, trackAddToCart, trackAddToWishlist } from '../utils/analytics';
import useSEO from '../hooks/useSEO';
import api from '../lib/api';
import { wishlistAPI } from '../services/api';
import { productImage, productImages, optimizedImage } from '../utils/productImage';
import { cleanDescription } from '../utils/cleanDescription';


const ProductDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const { addToGuestCart, isAuthenticated } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [ipayEnabled, setIpayEnabled] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);

  useEffect(() => {
    setAvailability(null);
    setSelectedSize(null);
    api.get(`/products/${id}/availability`)
      .then((r) => setAvailability(r.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    api.get('/payments/ipay/enabled').then(r => setIpayEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useSEO({
    title: product ? `${product.name} | R32.ro` : undefined,
    description: product
      ? `Cumpără ${product.name} pe R32.ro, marketplace online cu peste 250.000 de produse: fashion, haine de firmă, încălțăminte, ochelari de soare, produse pentru bebeluși și copii, cosmetice, parfumuri, IT, telefoane, huse, birotică și electronice. Livrare rapidă, plată securizată, retur în 14 zile.`
      : undefined,
    canonical: product ? `${window.location.origin}/product/${id}` : undefined,
    ogType: 'product',
  });

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      // Load product by ID
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      
      // Load related products from same category
      if (response.data.category) {
        const relatedResponse = await api.get('/products', {
          params: { category: response.data.category, limit: 5 }
        });
        // Filter out current product
        setRelatedProducts(relatedResponse.data.filter(p => p._id !== id).slice(0, 4));
      }
      
      // Track view_item event
      if (response.data) {
        trackViewItem(response.data);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut încărca produsul.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-12 rounded-2xl">
          <p className="text-gray-500 text-lg">Produsul nu a fost găsit.</p>
          <Link to="/catalog">
            <Button className="mt-4 bg-green-600 hover:bg-green-700 rounded-xl">
              Înapoi la Catalog
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  const sizeOptions = (availability?.sizes || []).filter((s) => s.size);
  const needsSize = sizeOptions.length > 0;
  const activeSize = needsSize
    ? (sizeOptions.find((s) => s.stockId === selectedSize) || null)
    : null;
  const shownPrice = activeSize?.price || product.price;

  const handleAddToCart = async () => {
    try {
      if (needsSize && !activeSize) {
        toast({ title: 'Alege mărimea', description: 'Selectează o mărime disponibilă.',
          variant: 'destructive' });
        return;
      }
      // Validate stock before adding to cart
      const availableStock = activeSize ? activeSize.stock : (product.stock || 0);
      const isInStock = activeSize ? activeSize.available : (product.inStock || availableStock > 0);
      
      if (!isInStock) {
        toast({
          title: 'Stoc epuizat',
          description: 'Din păcate, acest produs nu mai este disponibil.',
          variant: 'destructive',
        });
        return;
      }
      
      if (availableStock > 0 && quantity > availableStock) {
        toast({
          title: 'Cantitate indisponibilă',
          description: `Stoc disponibil: ${availableStock} bucăți. Te rugăm să reduci cantitatea.`,
          variant: 'destructive',
        });
        return;
      }
      
      if (isAuthenticated()) {
        // Logged in user - use API
        const { cartAPI } = await import('../services/api');
        await cartAPI.addItem({
          productId: product._id,
          quantity,
          ...(activeSize ? { stockId: activeSize.stockId, size: activeSize.size, color: activeSize.color } : {}),
        });
      } else {
        // Guest user - use local storage
        addToGuestCart(activeSize
          ? { ...product, price: activeSize.price || product.price, stockId: activeSize.stockId, size: activeSize.size, color: activeSize.color }
          : product, quantity);
      }
      
      // Track GA4 event
      trackAddToCart(product, quantity);
      
      // Trigger cart update event
      window.dispatchEvent(new Event('cartUpdated'));
      
      toast({
        title: 'Produs adăugat în coș!',
        description: `${quantity}x ${product.name} adăugat în coș.`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul în coș.',
        variant: 'destructive',
      });
    }
  };

  const handleAddToWishlist = async () => {
    console.log('[WISHLIST] Button clicked, product._id:', product._id);
    try {
      console.log('[WISHLIST] Calling API...');
      const result = await wishlistAPI.add({ productId: product._id });
      console.log('[WISHLIST] API success:', result);
      
      // Trigger wishlist update event
      window.dispatchEvent(new Event('wishlistUpdated'));
      
      toast({
        title: 'Adăugat la favorite!',
        description: `${product.name} a fost adăugat la lista ta de dorințe.`,
      });
    } catch (error) {
      console.error('[WISHLIST] Error adding to wishlist:', error);
      console.error('[WISHLIST] Error details:', error.response?.data);
      toast({
        title: 'Eroare',
        description: error.response?.data?.detail || 'Nu s-a putut adăuga produsul la favorite.',
        variant: 'destructive',
      });
    }
  };

  const mockReviews = [
    { id: 1, user: 'Ion Popescu', rating: 5, date: '2024-01-15', comment: 'Produs excelent! Recomandat cu încredere.' },
    { id: 2, user: 'Maria Ionescu', rating: 4, date: '2024-01-10', comment: 'Foarte bun raport calitate-preț.' },
    { id: 3, user: 'Andrei Gheorghe', rating: 5, date: '2024-01-05', comment: 'Livrare rapidă și produs ca în poze.' },
  ];

  // Build images array - use product.images if available, otherwise just the main image
  const allImages = productImages(
    product.images && product.images.length > 0
      ? [product.image, ...product.images.filter(img => img && img !== product.image)]
      : product.image ? [product.image] : [],
    product.image,
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-0" data-product-id={product._id} data-product-name={product.name}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <Link to="/" className="hover:text-green-600">Acasă</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/catalog" className="hover:text-green-600">Catalog</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-gray-900 font-semibold">{product.name}</span>
        </div>

        {/* Product Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Images */}
          <div>
            <Card className="p-4 rounded-2xl border-2 border-gray-100 mb-4 bg-white">
              <img
                src={optimizedImage(allImages[selectedImage] || product.image, 800)}
                alt={product.name}
                fetchPriority="high"
                decoding="async"
                onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.svg'; }}
                className="w-full h-60 object-contain rounded-xl sm:h-96"
              />
            </Card>
            {/* Only show thumbnails if there are multiple images */}
            {allImages.length > 1 && (
              <div className={`grid gap-4 ${allImages.length === 2 ? 'grid-cols-2' : allImages.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                {allImages.map((img, idx) => (
                  <Card
                    key={idx}
                    className={`p-2 rounded-xl cursor-pointer border-2 transition-all ${
                      selectedImage === idx ? 'border-green-600' : 'border-gray-100'
                    }`}
                    onClick={() => setSelectedImage(idx)}
                  >
                    <img src={optimizedImage(img, 200)} alt="" loading="lazy" onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.svg'; }} className="w-full h-20 object-contain rounded-lg" />
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge className="bg-gray-100 text-gray-700 rounded-xl mb-2">{product.brand}</Badge>
                {product.isNew && (
                  <Badge className="bg-green-600 text-white rounded-xl ml-2">NOU</Badge>
                )}
              </div>
              <button
                onClick={handleAddToWishlist}
                className="rounded-xl hover:bg-red-50 p-2 transition-colors"
                title="Adaugă la favorite"
              >
                <Heart className="h-6 w-6 text-gray-600 hover:text-red-500" />
              </button>
            </div>

            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
            {product.grade && (
              <div className="mb-4 flex items-center gap-3" data-testid="pdp-grade">
                <span className={`rounded-lg px-3 py-1.5 text-sm font-extrabold uppercase tracking-wide text-white ${
                  GRADE_STYLE[String(product.grade).charAt(0).toUpperCase()] || 'bg-gray-700'}`}>
                  Grad {product.grade}
                </span>
                <span className="text-sm text-gray-600">
                  {{ A: 'Stare excelentă, urme minime sau deloc', B: 'Stare foarte bună, urme ușoare de utilizare',
                     C: 'Stare bună, urme vizibile de utilizare' }[String(product.grade).charAt(0).toUpperCase()] || 'Telefon Pre-Owned verificat'}
                </span>
              </div>
            )}
            {(product.grade || product.category === 'telefoane-pre-owned') && <PreOwnedGuarantees />}
            {(product.source === 'babyproducts' || (product.category || '').includes('bebelusi')) && (
              <div className="mb-4"><AddToRegistryButton productId={product._id || product.id} /></div>
            )}

            {/* Rating */}
            <div className="flex items-center mb-6">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(product.rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-gray-600 ml-2">({product.reviews} recenzii)</span>
            </div>

            {/* Price */}
            {product.specialOrder ? (
            <Card className="p-6 rounded-2xl border-2 border-blue-200 bg-blue-50 mb-6" data-testid="cs-mention-detail">
              <p className="text-2xl font-bold text-blue-700">Furnizate la cerere</p>
              <p className="text-gray-600 mt-1">Acest produs este disponibil pe bază de comandă specială. Contactați R32.ro pentru preț și disponibilitate.</p>
            </Card>
            ) : (
            <Card className="p-6 rounded-2xl border-2 border-green-200 bg-green-50 mb-6">
              <div className="flex items-baseline justify-between">
                <div>
                  {product.oldPrice > 0 && (
                    <span className="text-lg text-gray-400 line-through mr-3">
                      {product.oldPrice} Lei
                    </span>
                  )}
                  <span className="text-4xl font-bold text-green-600" data-testid="pdp-price">{shownPrice} Lei</span>
                </div>
                {product.discount > 0 && (
                  <Badge className="bg-red-500 text-white rounded-xl px-4 py-2 text-lg">
                    -{product.discount}%
                  </Badge>
                )}
              </div>
              {ipayEnabled && (
                <div className="mt-4">
                  <StarRateBadge />
                </div>
              )}
            </Card>
            )}

            {/* Mărimi disponibile (stoc sincronizat cu furnizorul) */}
            {needsSize && (
              <div className="mb-6" data-testid="size-selector">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-4">
                    <p className="text-sm font-semibold text-gray-900">Mărime</p>
                    <SizeGuide sizes={sizeOptions} />
                  </div>
                  {availability?.updatedAt && (
                    <span className="text-xs text-gray-400" data-testid="size-freshness">
                      stoc actualizat {new Date(availability.updatedAt).toLocaleString('ro-RO', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {sizeOptions.map((s) => (
                    <button
                      key={s.stockId || s.size}
                      disabled={!s.available}
                      onClick={() => { setSelectedSize(s.stockId); setQuantity(1); }}
                      className={`relative min-w-[52px] rounded-xl border-2 px-3 py-2 text-sm font-semibold transition-colors ${
                        !s.available
                          ? 'cursor-not-allowed border-gray-100 text-gray-300 line-through'
                          : s.stockId === selectedSize
                            ? 'border-green-600 bg-green-50 text-green-800'
                            : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}
                      data-testid={`size-option-${s.size}`}
                    >
                      {s.size}
                      {s.available && s.lowStock && (
                        <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-orange-500" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                {activeSize ? (
                  <p className={`mt-2 text-sm font-medium ${activeSize.lowStock ? 'text-orange-600' : 'text-green-600'}`}
                     data-testid="size-stock-note">
                    {activeSize.lowStock
                      ? `⚠️ Ultimele ${activeSize.stock} bucăți la mărimea ${activeSize.size}`
                      : `✓ ${activeSize.stock} bucăți pe stoc la mărimea ${activeSize.size}`}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-500" data-testid="size-hint">
                    Alege mărimea pentru a vedea stocul disponibil.
                  </p>
                )}
                <SizeStockAlert productId={product._id} sizes={sizeOptions} />
              </div>
            )}

            {/* Quantity & Add to Cart / Special Order */}
            {product.specialOrder ? (
            <div className="mb-6">
              <Link to="/contact" data-testid="cs-request-offer-detail">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-6 text-lg font-semibold">
                  Cere ofertă — contactați R32.ro
                </Button>
              </Link>
            </div>
            ) : (
            <div className="flex items-center gap-3 mb-6 sm:space-x-4">
              <div className="flex items-center border-2 border-gray-200 rounded-xl">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  aria-label="Scade cantitatea"
                  className="flex h-12 w-12 items-center justify-center rounded-l-xl active:bg-gray-100"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center font-semibold sm:px-6 sm:w-auto">{quantity}</span>
                <button
                  onClick={() => {
                    const maxStock = (activeSize ? activeSize.stock : product.stock) || 999;
                    setQuantity(Math.min(maxStock, quantity + 1));
                  }}
                  aria-label="Crește cantitatea"
                  className="flex h-12 w-12 items-center justify-center rounded-r-xl active:bg-gray-100 disabled:opacity-40"
                  disabled={activeSize ? quantity >= activeSize.stock : (product.stock > 0 && quantity >= product.stock)}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={!product.inStock && !(product.stock && product.stock > 0)}
                data-testid="pdp-add-to-cart"
                className="h-12 flex-1 rounded-xl bg-green-600 text-base font-semibold text-white hover:bg-green-700 disabled:bg-gray-300 sm:py-6 sm:text-lg"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Adaugă în Coș
              </Button>
            </div>
            )}

            {/* Supplier & Stock Status */}
            <div className="mb-6">
              {/* Supplier Name */}
              {product.supplierName && (
                <p className="text-sm text-gray-500 mb-2">
                  Vândut de: <span className="font-medium text-gray-700">{product.supplierName}</span>
                </p>
              )}
              
              {/* Stock Status */}
              {!product.specialOrder && !needsSize && ((product.inStock || (product.stock && product.stock > 0)) ? (
                <div>
                  <p className="text-green-600 font-semibold" data-testid="stock-status-in-stock">
                    ✓ În stoc
                  </p>
                  {product.stockLabel === 'Stoc limitat' ? (
                    <p className="text-sm mt-1">
                      <span className="text-orange-600 font-medium" data-testid="stock-label-limited">⚠️ Stoc limitat</span>
                    </p>
                  ) : product.stockLabel ? (
                    <p className="text-sm text-gray-600 mt-1">
                      <span data-testid="stock-label-estimated">{product.stockLabel}</span>
                    </p>
                  ) : product.stock > 0 && !product.stockLabel && (
                    <p className="text-sm text-gray-600 mt-1">
                      {product.stock <= 5 ? (
                        <span className="text-orange-600 font-medium">⚠️ Ultimele {product.stock} bucăți disponibile!</span>
                      ) : (
                        <span>{product.stock} bucăți disponibile</span>
                      )}
                    </p>
                  )}
                </div>
              ) : (
                product.plannedDelivery ? (
                  <p className="text-amber-700 font-semibold" data-testid="stock-status-planned-delivery">
                    Disponibil de la {new Date(product.plannedDelivery).toLocaleDateString('ro-RO')}
                  </p>
                ) : (
                  <p className="text-red-600 font-semibold" data-testid="stock-status-out-of-stock">✗ Stoc epuizat</p>
                )
              ))}
            </div>

            {/* Features */}
            <div className="space-y-4">
              {product.shippingOnRequest ? (
                <div className="flex items-center space-x-3" data-testid="product-shipping-on-request">
                  <Truck className="h-6 w-6 text-amber-600" />
                  <div>
                    <p className="font-semibold">Transport</p>
                    <p className="text-sm text-gray-600">
                      Va fi comunicat la confirmarea telefonică a comenzii
                    </p>
                  </div>
                </div>
              ) : !product.specialOrder && (
              <div className="flex items-center space-x-3">
                <Truck className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold">Livrare gratuită</p>
                  <p className="text-sm text-gray-600">Pentru comenzi peste 400 Lei</p>
                </div>
              </div>
              )}
              <div className="flex items-center space-x-3">
                <Shield className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold">{product.grade ? 'Garanție 12 luni' : 'Garanție 2 ani'}</p>
                  <p className="text-sm text-gray-600">Conform legislației în vigoare</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <RotateCcw className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-semibold">Returnare 30 zile</p>
                  <p className="text-sm text-gray-600">Fără costuri suplimentare</p>
                </div>
              </div>
            </div>

            {/* Notificarea armonizată UE privind garanția legală + eticheta GARAN (Reg. UE 2025/1960) */}
            <div className="mt-4 space-y-3">
              <EuGuaranteeNotice testId="pdp-eu-guarantee-notice" />
              <GaranLabel years={product.durabilityYears} brand={product.durabilityBrand || product.brand}
                          model={product.durabilityModel} labelUrl={product.garanLabelUrl} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="description" className="mb-12">
          <TabsList className="rounded-xl border-2 border-gray-100">
            <TabsTrigger value="description" className="rounded-xl">Descriere</TabsTrigger>
            <TabsTrigger value="specs" className="rounded-xl">Specificații</TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-xl">Recenzii ({product.reviews})</TabsTrigger>
          </TabsList>

          <TabsContent value="description">
            <Card className="p-6 rounded-2xl">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {cleanDescription(product.description) || 'Descriere detaliată a produsului. Acest produs oferă caracteristici excelente și performanță de înaltă calitate.'}
              </p>
            </Card>
          </TabsContent>

          <TabsContent value="specs">
            <Card className="p-6 rounded-2xl">
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold">Brand:</span>
                  <span>{product.brand}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold">Model:</span>
                  <span>{product.name}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-semibold">Cod produs:</span>
                  <span>R32-{product._id.substring(0, 8).toUpperCase()}</span>
                </div>
                {Object.entries(product.specifications || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2 border-b" data-testid="product-spec-row">
                    <span className="font-semibold">{k}:</span>
                    <span className="text-right">{String(v)}</span>
                  </div>
                ))}
              </div>
              {product.documents?.length > 0 && (
                <div className="mt-6" data-testid="product-documents">
                  <p className="font-semibold mb-2">Fișe tehnice</p>
                  <ul className="space-y-1">
                    {product.documents.map((d, i) => (
                      <li key={d.url || i}>
                        <a href={d.url} target="_blank" rel="noopener noreferrer"
                          className="text-green-700 underline hover:text-green-900" data-testid={`product-document-${i}`}>
                          {d.name || 'Fișă tehnică'}{(d.mimeType || '').includes('pdf') || /\.pdf$/i.test(d.url || '') ? ' (PDF)' : ''}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="reviews">
            <Card className="p-6 rounded-2xl">
              <div className="space-y-6">
                {mockReviews.map(review => (
                  <div key={review.id} className="border-b pb-6 last:border-b-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold">{review.user}</span>
                      <span className="text-sm text-gray-500">{review.date}</span>
                    </div>
                    <div className="flex items-center mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < review.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-gray-700">{review.comment}</p>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        <section>
          <h2 className="text-xl font-bold mb-4 sm:text-2xl sm:mb-6">Produse Similare</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-6">
            {relatedProducts.map(product => (
              <ProductCard
                key={product._id}
                product={product}
                onAddToCart={() => handleAddToCart(product)}
                onAddToWishlist={() => handleAddToWishlist(product)}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Mobile: bară fixă cu preț + adăugare în coș (deasupra navigației de jos) */}
      {!product.specialOrder && (
        <div className="mobile-sticky-bar fixed bottom-14 left-0 right-0 z-30 flex items-center gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden" data-testid="pdp-mobile-bar">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-gray-500">Preț</p>
            <p className="text-lg font-bold text-green-600" data-testid="pdp-mobile-price">{shownPrice} Lei</p>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={!product.inStock && !(product.stock && product.stock > 0)}
            className="h-12 flex-1 rounded-xl bg-green-600 text-sm font-semibold hover:bg-green-700 disabled:bg-gray-300"
            data-testid="pdp-mobile-add-to-cart"
          >
            <ShoppingCart className="mr-2 h-5 w-5" />
            {(product.inStock || (product.stock && product.stock > 0)) ? 'Adaugă în coș' : 'Indisponibil'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
